/**
 * עורך תבניות דוח חברים + רקע מכתב A4 עם תצוגה מקדימה ומרווחים
 */
import React from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Chip, Typography, Alert, List, ListItem, ListItemText,
  IconButton, MenuItem, Select, FormControl, InputLabel, Paper, Divider,
  Slider, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import { useMemberReportTemplateEditor, TARGET_TYPE_LABELS, INSET_FIELDS } from './useMemberReportTemplateEditor.js';
import MemberReportTemplateEditorNext from './MemberReportTemplateEditorNext.jsx';
import {
  MERGE_FIELDS,
  mergeFieldSpanHtml,
  defaultTemplateForType,
} from './memberReportMergeFields.js';
import { DESIGN_ELEMENTS, blankFieldSpanHtml, REPORT_BLANK_PRINT_CSS } from './memberReportBlanks.js';
import {
  letterPageCss,
} from './memberPrint.js';

/** In the new design the same props render MemberReportTemplateEditorNext. */
export default function MemberReportTemplateEditor(props) {
  return useCkInside() ? <MemberReportTemplateEditorNext {...props} /> : <MemberReportTemplateEditorLegacy {...props} />;
}

function MemberReportTemplateEditorLegacy(props) {
  const {
    templates,
    selectedId,
    name,
    setName,
    targetType,
    setTargetType,
    setBodyHtml,
    previewHtml,
    feedback,
    setFeedback,
    loading,
    blankMeta,
    blankPreviewUrl,
    letterInsets,
    insetsDirty,
    savingInsets,
    editorRef,
    fileRef,
    syncFromEditor,
    saveInsets,
    handleInsetChange,
    insertHtml,
    selectTemplate,
    handleNew,
    handleSave,
    handleDelete,
    handleBlankUpload,
    handleBlankRemove,
    hasBlank,
    previewPageClass,
    previewBgStyle,
    open,
    onClose,
  } = useMemberReportTemplateEditor(props);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: 'min(1280px, 98vw)',
          height: 'min(920px, 96vh)',
          maxHeight: '96vh',
          m: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1, flexShrink: 0 }}>
        עיצוב דוח — תבנית ורקע מכתב
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', py: 1.5 }}>
        {feedback && (
          <Alert
            severity={feedback.includes('שגיאה') ? 'error' : 'success'}
            sx={{ mb: 1.5, flexShrink: 0 }}
            onClose={() => setFeedback('')}
          >
            {feedback}
          </Alert>
        )}

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ mb: 1.5, flexShrink: 0, py: 0.5, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.55 }}>
            <strong>רקע מכתב</strong> — תמונת A4 מאחורי הטקסט (לוגו / כותרת).{' '}
            <strong>תבנית</strong> — הטקסט והשדות שמעל הרקע.{' '}
            <strong>קווי כתיבה</strong> — קווים לחתימה/הערות בתוך התבנית (לא חלק מהתמונה).{' '}
            <strong>מרווחים</strong> — הזיזו את התוכן עד שהוא יושב נכון מול הלוגו בתמונה.
          </Typography>
        </Alert>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(340px, 420px) minmax(0, 1fr)' },
            gap: 2,
            overflow: 'hidden',
          }}
        >
          {/* עמודה שמאלית — תבניות, רקע, עורך */}
          <Box sx={{ minHeight: 0, overflow: 'auto', pr: 0.5 }}>
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={600}>תבניות</Typography>
                  <Button size="small" onClick={handleNew}>חדשה</Button>
                </Stack>
                <List dense sx={{ maxHeight: 140, overflow: 'auto' }}>
                  {templates.map((t) => (
                    <ListItem
                      key={t._id}
                      selected={String(t._id) === String(selectedId)}
                      onClick={() => selectTemplate(t)}
                      secondaryAction={
                        String(t._id) === String(selectedId) ? (
                          <IconButton edge="end" size="small" onClick={handleDelete}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : null
                      }
                      sx={{ cursor: 'pointer', borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={t.name}
                        secondary={TARGET_TYPE_LABELS[t.targetType] || t.targetType}
                      />
                    </ListItem>
                  ))}
                  {!templates.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      אין תבניות עדיין
                    </Typography>
                  )}
                </List>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography fontWeight={600} sx={{ mb: 0.5 }}>רקע מכתב (תמונת A4)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  התמונה מופיעה כרקע לעמוד המכתב בהדפסה. כוונו מרווחים בתצוגה הגדולה מימין.
                </Typography>
                {hasBlank && (
                  <Box
                    component="img"
                    src={blankPreviewUrl}
                    alt="רקע מכתב"
                    sx={{
                      width: '100%',
                      maxHeight: 100,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                      bgcolor: 'var(--ck-surface-2, #fafafa)',
                    }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {blankMeta
                    ? `פעיל: ${blankMeta.fileOriginalName || blankMeta.name}`
                    : 'לא הוגדר רקע מכתב'}
                </Typography>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={handleBlankUpload}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileRef.current?.click()}
                    disabled={loading}
                  >
                    העלה תמונה
                  </Button>
                  {blankMeta && (
                    <Button size="small" color="error" onClick={handleBlankRemove} disabled={loading}>
                      הסר
                    </Button>
                  )}
                </Stack>

                {blankMeta && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography fontWeight={600} variant="body2">מרווחי תוכן (מ״מ)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {savingInsets ? 'שומר…' : insetsDirty ? 'ממתין לשמירה…' : 'נשמר'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      הגדילו את העליון אם יש לוגו גדול בראש התמונה.
                    </Typography>
                    {INSET_FIELDS.map((f) => (
                      <Box key={f.key} sx={{ mb: 0.75 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption">{f.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {letterInsets[f.key]} מ״מ
                          </Typography>
                        </Stack>
                        <Slider
                          size="small"
                          min={0}
                          max={f.max}
                          step={1}
                          value={letterInsets[f.key]}
                          onChange={(_e, v) => handleInsetChange(f.key, v)}
                          valueLabelDisplay="auto"
                        />
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => saveInsets(letterInsets)}
                      disabled={savingInsets || !insetsDirty}
                      fullWidth
                    >
                      שמור מרווחים
                    </Button>
                  </>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1.25}>
                  <TextField
                    size="small"
                    label="שם תבנית"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>סוג</InputLabel>
                    <Select
                      label="סוג"
                      value={targetType}
                      onChange={(e) => {
                        setTargetType(e.target.value);
                        if (!selectedId) setBodyHtml(defaultTemplateForType(e.target.value));
                      }}
                    >
                      <MenuItem value="list">רשימה</MenuItem>
                      <MenuItem value="breakdown">פילוח</MenuItem>
                      <MenuItem value="card">כרטיס נבחן</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography variant="caption" color="text.secondary">שדות מיזוג</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {MERGE_FIELDS.map((f) => (
                      <Chip
                        key={f.key}
                        size="small"
                        label={f.label}
                        onClick={() => insertHtml(mergeFieldSpanHtml(f.key))}
                      />
                    ))}
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">קווי כתיבה / חתימה</Typography>
                    <Tooltip title="קווים ריקים בתוך התבנית למילוי ידני — לא חלק מתמונת הרקע">
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Tooltip>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {DESIGN_ELEMENTS.map((f) => (
                      <Chip
                        key={f.key}
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={f.label}
                        onClick={() => insertHtml(blankFieldSpanHtml(f.key))}
                      />
                    ))}
                  </Box>

                  <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={syncFromEditor}
                    onInput={syncFromEditor}
                    sx={{
                      minHeight: 160,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      direction: 'rtl',
                      fontFamily: '"David", "Arial Hebrew", Arial, sans-serif',
                      lineHeight: 1.7,
                      outline: 'none',
                      bgcolor: '#fff',
                      '&:focus': { borderColor: 'primary.main' },
                    }}
                  />
                  <Button variant="contained" onClick={handleSave} disabled={loading}>
                    שמור תבנית
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </Box>

          {/* עמודה ימנית — תצוגת A4 גדולה */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: '#e8eaed',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
              <Typography fontWeight={700}>תצוגה מקדימה</Typography>
              <Typography variant="caption" color="text.secondary">
                כך ייצא בהדפסה (עמוד מכתב)
              </Typography>
            </Stack>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                py: 1,
              }}
            >
              <Box
                sx={{
                  width: 'min(100%, 520px)',
                  aspectRatio: '210 / 297',
                  flexShrink: 0,
                  boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  bgcolor: '#fff',
                }}
              >
                <style>{letterPageCss(letterInsets)}</style>
                <style>{REPORT_BLANK_PRINT_CSS}</style>
                <style>{`
                  .letter-preview-shell .letter-page {
                    min-height: 100% !important;
                    height: 100%;
                    page-break-after: auto;
                    font-family: "David", "Arial Hebrew", Arial, sans-serif;
                    direction: rtl;
                    color: #111;
                    font-size: 11pt;
                    line-height: 1.55;
                  }
                  .letter-preview-shell .letter-page.has-blank .letter-body {
                    min-height: 100% !important;
                  }
                `}</style>
                <div className="letter-preview-shell" style={{ height: '100%' }}>
                  <section
                    className={previewPageClass}
                    style={{
                      ...previewBgStyle,
                      height: '100%',
                      minHeight: '100%',
                    }}
                  >
                    <div
                      className="letter-body"
                      dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#888">הקלידו תוכן בתבנית…</p>' }}
                    />
                  </section>
                </div>
              </Box>
            </Box>
            {!hasBlank && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', flexShrink: 0 }}>
                אין רקע מכתב — העלו תמונת A4 משמאל כדי לראות אותה כאן
              </Typography>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose}>סגור</Button>
      </DialogActions>
    </Dialog>
  );
}
